import React, { useEffect, useState } from "react";

import {
  Check,
  X,
  Calendar as CalendarIcon,
  FileDown,
  Paperclip,
  Edit,
  Ban,
  Eye,
} from "lucide-react";

import leaveService from "../../services/leaveService";
import apiClient from "../../services/api_client";
import LeaveApprovedDetailModal from "../leave/LeaveApprovedDetailModal";

import {
  formatDurationDays,
  getDayPartLabel,
  isHalfDay,
  getStatusLabel,
} from "../../utils/leaveDisplay";


const LeaveRequestTable = ({ requests, role, onAction }) => {

  const [downloadingId, setDownloadingId] = useState(null);
  const [pdfError, setPdfError] = useState("");
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [attachmentsByRequest, setAttachmentsByRequest] = useState({});
  const [attachmentsLoading, setAttachmentsLoading] = useState({});

  // Ouvrir les détails d'une demande pour tous les rôles.
  const handleRowClick = (request) => {
    setSelectedLeave(request);
    setShowDetailModal(true);
  };

  const handleModalClose = () => {
    setShowDetailModal(false);
    setSelectedLeave(null);
  };


  // =========================================================
  // STATUT
  // =========================================================

  // Statut métier affiché dans la colonne : les étapes ne sont affichées
  // que tant que la demande attend une décision. Après décision RH,
  // le statut final est « Validée » ou « Refusée ».
  const getStatusBadge = (status) => {
    const normalizedStatus = String(status || "").trim().toUpperCase();

    const statusConfig = {
      PENDING_MANAGER: {
        label: "En attente du manager",
        className: "badge badge-warning",
      },
      PENDING_HR: {
        label: "En attente de validation RH",
        className: "badge badge-info",
      },
      APPROVED: {
        label: "Validée",
        className: "badge badge-success",
      },
      REJECTED: {
        label: "Refusée",
        className: "badge badge-danger",
      },
      CANCELLED: {
        label: "Annulée",
        className: "badge badge-danger",
      },
    };

    const config = statusConfig[normalizedStatus];
    if (!config) {
      return <span className="badge">{getStatusLabel(status) || "-"}</span>;
    }

    return <span className={config.className}>{config.label}</span>;
  };


  // =========================================================
  // DATE
  // =========================================================

  const formatDate = (dateStr) => {

    if (!dateStr) return "";

    const date = new Date(dateStr);

    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };


  // =========================================================
  // ACTIONS MANAGER / RH
  // =========================================================

  const showActions = (req) => {

    if (
      role === "manager" &&
      req.status === "PENDING_MANAGER"
    ) {
      return true;
    }

    if (
      role === "rh" &&
      req.status === "PENDING_HR"
    ) {
      return true;
    }

    return false;
  };


  // =========================================================
  // RH : LECTURE SEULE SI ATTENTE MANAGER
  // =========================================================

  const isReadOnlyForRH = (req) => {

    return (
      role === "rh" &&
      req.status === "PENDING_MANAGER"
    );
  };


  // =========================================================
  // VERIFIER SI L'EMPLOYE PEUT MODIFIER
  // =========================================================

  const canEdit = (req) => {

    if (role !== "employee") {
      return false;
    }

    /*
      L'employé peut modifier uniquement :

      - PENDING_MANAGER
      - PENDING_HR

      Donc avant validation finale.
    */

    return (
      req.status === "PENDING_MANAGER" ||
      req.status === "PENDING_HR"
    );
  };


  // =========================================================
  // VERIFIER SI L'EMPLOYE PEUT ANNULER
  // =========================================================

  const canCancel = (req) => {

    if (role !== "employee") {
      return false;
    }

    /*
      L'employé peut annuler uniquement
      tant que la demande n'est pas validée.
    */

    return (
      req.status === "PENDING_MANAGER" ||
      req.status === "PENDING_HR"
    );
  };


  // =========================================================
  // PIECE JOINTE
  // =========================================================

  const handleDownloadAttachment = async (requestId) => {

    try {

      const response = await leaveService.downloadAttachment(requestId);

      const url =
        window.URL.createObjectURL(response);

      const link =
        document.createElement("a");

      link.href = url;

      link.download =
        `piece_jointe_${requestId}`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);

    } catch (err) {

      console.error(
        "Erreur téléchargement pièce jointe :",
        err
      );

    }
  };


  // =========================================================
  // TYPE DE CONGE
  // =========================================================

  const getLeaveTypeTag = (req) => {

    const code =
      (req.leave_type_code || "").toUpperCase();

    switch (code) {

      case "ANNUEL":

        return {
          label:
            req.leave_type_name ||
            "Congé annuel",

          classSuffix:
            "annuel",
        };


      case "MALADIE":

        return {
          label:
            req.leave_type_name ||
            "Congé maladie",

          classSuffix:
            "maladie",
        };


      case "REPOS":

        return {
          label:
            req.leave_type_name ||
            "Repos",

          classSuffix:
            "rtt",
        };


      case "SPECIAL":

        return {
          label:
            req.leave_type_name ||
            "Congé spécial",

          classSuffix:
            "sans-solde",
        };


      default:

        return {
          label:
            req.leave_type_name || "-",

          classSuffix: "",
        };
    }
  };

   
  // =========================================================
  // RH : CERTIFICAT MÉDICAL OBLIGATOIRE
  // Vérifie aussi les pièces jointes complémentaires.
  // =========================================================

  useEffect(() => {
    let cancelled = false;

    const loadAttachments = async () => {
      const medicalRequests = (requests || []).filter(
        (req) =>
          role === "rh" &&
          req.status === "PENDING_HR" &&
          (req.leave_type_code || "").toUpperCase() === "MALADIE"
      );

      if (medicalRequests.length === 0) return;

      const loadingMap = {};
      medicalRequests.forEach((req) => {
        loadingMap[req.id] = true;
      });
      setAttachmentsLoading((previous) => ({ ...previous, ...loadingMap }));

      await Promise.all(
        medicalRequests.map(async (req) => {
          try {
            const response = await apiClient.get(
              `/leave-requests/${req.id}/attachments`
            );
            const payload = response?.data;
            const attachments = Array.isArray(payload)
              ? payload
              : Array.isArray(payload?.attachments)
                ? payload.attachments
                : Array.isArray(payload?.results)
                  ? payload.results
                  : [];

            if (!cancelled) {
              setAttachmentsByRequest((previous) => ({
                ...previous,
                [req.id]: attachments.length > 0,
              }));
            }
          } catch (error) {
            console.error(
              `Impossible de vérifier les pièces jointes de la demande ${req.id}`,
              error
            );
            if (!cancelled) {
              setAttachmentsByRequest((previous) => ({
                ...previous,
                [req.id]: false,
              }));
            }
          } finally {
            if (!cancelled) {
              setAttachmentsLoading((previous) => ({
                ...previous,
                [req.id]: false,
              }));
            }
          }
        })
      );
    };

    loadAttachments();
    return () => {
      cancelled = true;
    };
  }, [requests, role]);

  const hasMedicalCertificate = (req) =>
    Boolean(req.attachment_path || attachmentsByRequest[req.id]);

  const isMedicalCertificateMissing = (req) =>
    role === "rh" &&
    (req.leave_type_code || "").toUpperCase() === "MALADIE" &&
    !hasMedicalCertificate(req);

  const isMedicalCertificateCheckPending = (req) =>
    role === "rh" &&
    req.status === "PENDING_HR" &&
    (req.leave_type_code || "").toUpperCase() === "MALADIE" &&
    attachmentsLoading[req.id] === true &&
    !req.attachment_path;

  // =========================================================
  // PDF
  // =========================================================

const handleDownloadPdf = async (requestId) => {

    setPdfError("");

    setDownloadingId(requestId);

    try {

      const blob =
        await leaveService.downloadLeavePdf(
          requestId
        );

      const url =
        window.URL.createObjectURL(blob);

      const link =
        document.createElement("a");

      link.href = url;

      link.download =
        `conge_${requestId}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);

    } catch (err) {

      console.error(
        "Erreur génération PDF :",
        err
      );

      const detail =
        err.response?.data?.detail;

      setPdfError(
        detail ||
        "Impossible de générer le PDF. Veuillez réessayer."
      );

    } finally {

      setDownloadingId(null);
    }
  };


  // =========================================================
  // AUCUNE DEMANDE
  // =========================================================

  if (!requests || requests.length === 0) {

    return (
      <div className="empty-state-container">

        <CalendarIcon
          size={40}
          className="empty-state-icon"
        />

        <p className="empty-state-text">
          Aucune demande de congé à afficher.
        </p>

      </div>
    );
  }


  // =========================================================
  // TABLE
  // =========================================================

  return (
    <>

      {pdfError && (
        <div
          className="form-error-box"
          style={{
            marginBottom: "12px",
          }}
        >
          <span>{pdfError}</span>
        </div>
      )}


      <div className="table-responsive-wrapper">

        <table className="custom-table">

          <thead>

            <tr>

              {role !== "employee" && (
                <th>
                  Collaborateur
                </th>
              )}

              {role !== "employee" && (
                <th>
                  Département
                </th>
              )}

              <th>
                Type
              </th>

              <th>
                Dates
              </th>

              <th>
                Durée
              </th>

              <th>
                Motif
              </th>

              <th>
                Statut
              </th>
              <th>Pièces jointes</th>

              <th>
                {role === "employee"
                  ? "Actions"
                  : "Actions"}
              </th>

            </tr>

          </thead>


          <tbody>

            {requests.map((req) => (

              <tr
                key={req.id}
                onClick={() => handleRowClick(req)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleRowClick(req);
                  }
                }}
                tabIndex={0}
                title="Cliquer pour consulter les détails de la demande"
                style={{ cursor: "pointer" }}
              >

                {/* =================================================
                    COLLABORATEUR
                ================================================= */}

                {role !== "employee" && (

                  <td>

                    <div className="table-user-cell">

                      <span className="user-initials">

                        {req.employee_name
                          ? req.employee_name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                          : "EMP"}

                      </span>

                      <span className="user-name">
                        {req.employee_name}
                      </span>

                    </div>

                  </td>
                )}


                {/* =================================================
                    DEPARTEMENT
                ================================================= */}

                {role !== "employee" && (

                  <td>
                    {req.department || "-"}
                  </td>

                )}


                {/* =================================================
                    TYPE
                ================================================= */}

                <td>

                  {(() => {

                    const tag =
                      getLeaveTypeTag(req);

                    return (
                      <span
                        className={
                          `type-tag type-${tag.classSuffix}`
                        }
                      >
                        {tag.label}
                      </span>
                    );

                  })()}

                </td>


                {/* =================================================
                    DATES
                ================================================= */}

                <td>

                  <div className="date-range-cell">

                    {isHalfDay(req.day_part) ? (

                      <span>
                        {formatDate(
                          req.start_date
                        )}
                      </span>

                    ) : (

                      <>
                        <span>
                          Du{" "}
                          {formatDate(
                            req.start_date
                          )}
                        </span>

                        <span className="date-separator">
                          au
                        </span>

                        <span>
                          {formatDate(
                            req.end_date
                          )}
                        </span>
                      </>

                    )}

                  </div>


                  {isHalfDay(req.day_part) && (

                    <div
                      style={{
                        fontSize: "0.78rem",
                        color: "#64748b",
                        marginTop: "4px",
                      }}
                    >
                      {getDayPartLabel(
                        req.day_part
                      )}
                    </div>

                  )}

                </td>


                {/* =================================================
                    DUREE
                ================================================= */}

                <td className="text-center font-semibold">

                  {formatDurationDays(
                    req.duration_days
                  )}

                </td>


                {/* =================================================
                    MOTIF
                ================================================= */}

                <td
                  className="reason-cell"
                  title={req.reason}
                >
                  {req.reason || "-"}
                </td>


                {/* =================================================
                    STATUT
                ================================================= */}

                <td>
                  {getStatusBadge(
                    req.status
                  )}
                </td>

                <td style={{ textAlign: "center" }}>
                  {req.attachment_count !== undefined &&
                  req.attachment_count !== null ? (
                    req.attachment_count > 0 ? (
                      <span style={{ color: "var(--primary)", fontWeight: 600 }}>
                        {req.attachment_count} fichier
                        {req.attachment_count > 1 ? "s" : ""}
                      </span>
                    ) : (
                      <span style={{ color: "var(--text-light)" }}>Aucune</span>
                    )
                  ) : attachmentsLoading[req.id] ? (
                    <span style={{ color: "var(--text-muted)" }}>Vérification…</span>
                  ) : attachmentsByRequest[req.id] ? (
                    <span style={{ color: "var(--primary)", fontWeight: 600 }}>
                      Fichier présent
                    </span>
                  ) : req.attachment_path ? (
                    <span style={{ color: "var(--primary)", fontWeight: 600 }}>
                      Fichier présent
                    </span>
                  ) : (
                    <span style={{ color: "var(--text-light)" }}>Aucune</span>
                  )}
                </td>
                {/* =================================================
                    ACTIONS
                ================================================= */}

                <td>

                  {/* =================================================
                      EMPLOYE
                  ================================================= */}

                  {role === "employee" ? (

                    <div
                      className="table-action-buttons"
                      style={{
                        gap: "6px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >

                      {/* DETAILS DE LA DEMANDE */}
                      <button
                        type="button"
                        onClick={() => handleRowClick(req)}
                        className="btn-action"
                        title="Voir les détails de la demande"
                        aria-label="Voir les détails de la demande"
                        style={{ background: "#64748b" }}
                      >
                        <Eye size={14} />
                      </button>

                      {/* PIECE JOINTE */}

                      {req.attachment_path && (

                        <button
                          onClick={() =>
                            handleDownloadAttachment(
                              req.id
                            )
                          }
                          className="btn-action"
                          title="Télécharger la pièce jointe"
                          style={{
                            background:
                              "#3b82f6",
                          }}
                        >
                          <Paperclip size={14} />
                        </button>

                      )}


                      {/* PDF */}

                      {req.status === "APPROVED" && (

                        <button
                          onClick={() =>
                            handleDownloadPdf(
                              req.id
                            )
                          }
                          disabled={
                            downloadingId === req.id
                          }
                          className="btn btn-outline btn-icon-group"
                          style={{
                            fontSize:
                              "0.82rem",
                            padding:
                              "6px 10px",
                          }}
                          title="Enregistrer en PDF"
                        >

                          <FileDown
                            size={14}
                          />

                          <span>
                            {downloadingId ===
                            req.id
                              ? "..."
                              : "PDF"}
                          </span>

                        </button>

                      )}


                      {/* =================================================
                          MODIFIER
                      ================================================= */}

                      {canEdit(req) &&
                        onAction && (

                          <button
                            onClick={() =>
                              onAction(
                                req.id,
                                "Modifier"
                              )
                            }
                            className="btn-action"
                            title="Modifier la demande"
                            style={{
                              background:
                                "#f59e0b",
                            }}
                          >

                            <Edit size={14} />

                          </button>

                        )}


                      {/* =================================================
                          ANNULER
                      ================================================= */}

                      {canCancel(req) &&
                        onAction && (

                          <button
                            onClick={() =>
                              onAction(
                                req.id,
                                "Annuler"
                              )
                            }
                            className="btn-action"
                            title="Annuler la demande"
                            style={{
                              background:
                                "#ef4444",
                            }}
                          >

                            <Ban size={14} />

                          </button>

                        )}


                      {/* Aucun bouton */}

                      {!req.attachment_path &&
                        req.status !== "APPROVED" &&
                        !canEdit(req) &&
                        !canCancel(req) && (

                          <span
                            style={{
                              color:
                                "#94a3b8",
                              fontSize:
                                "0.8rem",
                            }}
                          >
                            —
                          </span>

                        )}

                    </div>

                  ) : (

                    /* =================================================
                       MANAGER / RH
                    ================================================= */

                    isReadOnlyForRH(req) ? (

                      <span
                        style={{
                          fontSize:
                            "0.75rem",
                          color:
                            "#6b7280",
                          fontStyle:
                            "italic",
                        }}
                      >
                        En attente Manager
                      </span>

                    ) : showActions(req) ? (

                      <div
                        className="table-action-buttons"
                        style={{
                          gap: "6px",
                        }}
                      >

                       
                      {/* APPROUVER */}

                      <button
                        type="button"
                        onClick={(event) => {
                          // Le bouton agit uniquement sur la demande :
                          // ne pas déclencher le clic du <tr> qui ouvre la modale.
                          event.stopPropagation();

                          if (
                            isMedicalCertificateMissing(req) ||
                            isMedicalCertificateCheckPending(req)
                          ) {
                            return;
                          }

                          onAction(req.id, "Approuver");
                        }}
                        disabled={
                          isMedicalCertificateMissing(req) ||
                          isMedicalCertificateCheckPending(req)
                        }
                        className="btn-action btn-action-approve"
                        title={
                          isMedicalCertificateCheckPending(req)
                            ? "Vérification du certificat médical..."
                            : isMedicalCertificateMissing(req)
                              ? "Validation impossible : certificat médical manquant"
                            : role === "manager"
                              ? "Valider en tant que Manager"
                              : "Approuver définitivement"
                        }
                        style={{
                          opacity:
                            isMedicalCertificateMissing(req) ||
                            isMedicalCertificateCheckPending(req)
                              ? 0.4
                              : 1,
                          cursor:
                            isMedicalCertificateMissing(req) ||
                            isMedicalCertificateCheckPending(req)
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        <Check size={16} />
                      </button>


                        {/* REFUSER */}

                        <button
                          type="button"
                          onClick={(event) => {
                            // Évite d'ouvrir les détails en même temps que le refus.
                            event.stopPropagation();
                            onAction(req.id, "Refuser");
                          }}
                          className="btn-action btn-action-reject"
                          title="Refuser"
                        >

                          <X size={16} />

                        </button>

                        {isMedicalCertificateMissing(req) && (
                          <span
                            role="alert"
                            style={{
                              color: "#b91c1c",
                              background: "#fef2f2",
                              border: "1px solid #fecaca",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              fontSize: "12px",
                              fontWeight: 600,
                            }}
                          >
                            Certificat médical obligatoire avant validation.
                          </span>
                        )}

                      </div>

                    ) : (

                      <div
                        className="table-action-buttons"
                        style={{
                          gap: "6px",
                        }}
                      >
                         
                      {isMedicalCertificateMissing(req) && (
                        <div
                          role="alert"
                          style={{
                            marginTop: "8px",
                            color: "#b91c1c",
                            background: "#fef2f2",
                            border: "1px solid #fecaca",
                            borderRadius: "6px",
                            padding: "8px",
                            fontSize: "12px",
                            fontWeight: 600,
                            maxWidth: "240px",
                          }}
                        >
                          ⚠ Certificat médical absent.
                          Validation RH impossible.
                        </div>
                      )}

                        {req.attachment_path && (

                          <button
                            onClick={() =>
                              handleDownloadAttachment(
                                req.id
                              )
                            }
                            className="btn-action"
                            title="Télécharger la pièce jointe"
                            style={{
                              background:
                                "#3b82f6",
                            }}
                          >

                            <Paperclip
                              size={14}
                            />

                          </button>

                        )}

                        {req.status ===
                          "APPROVED" && (

                          <button
                            onClick={() =>
                              handleDownloadPdf(
                                req.id
                              )
                            }
                            disabled={
                              downloadingId ===
                              req.id
                            }
                            className="btn btn-outline btn-icon-group"
                            style={{
                              fontSize:
                                "0.82rem",
                              padding:
                                "6px 10px",
                            }}
                          >

                            <FileDown
                              size={14}
                            />

                            <span>
                              {downloadingId ===
                              req.id
                                ? "..."
                                : "PDF"}
                            </span>

                          </button>

                        )}

                      </div>

                    )

                  )}

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {showDetailModal && selectedLeave && (
        <LeaveApprovedDetailModal
          leave={selectedLeave}
          onClose={handleModalClose}
        />
      )}

    </>
  );
};


export default LeaveRequestTable;